/**
 * ClearButton Component
 * Reusable clear button for form fields
 * Implements proper accessibility and event handling
 */

import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { stopPropagation, handleKeyboardActivation } from './utils';

// ============================================================================
// Styled Components
// ============================================================================

const ClearButtonContainer = styled.span<{
  position?: 'absolute' | 'relative';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.position === 'absolute' &&
    `
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  `}
`;

const ClearButtonElement = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-muted);
  font-size: 0.875rem;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    color: var(--danger-600);
    background-color: var(--danger-50);
  }

  &:focus {
    outline: none;
    color: var(--danger-600);
    background-color: var(--danger-100);
    box-shadow: 0 0 0 2px var(--danger-100);
  }

  &:active {
    transform: scale(0.95);
  }

  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

// ============================================================================
// Component Interface
// ============================================================================

export interface ClearButtonProps {
  onClear: () => void;
  disabled?: boolean;
  position?: 'absolute' | 'relative';
  'aria-label'?: string;
  className?: string;
  testId?: string;
}

// ============================================================================
// ClearButton Component
// ============================================================================

export const ClearButton = forwardRef<HTMLSpanElement, ClearButtonProps>(
  (
    {
      onClear,
      disabled = false,
      position = 'relative',
      'aria-label': ariaLabel = 'Limpiar selección',
      className,
      testId,
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent) => {
      if (disabled) return;
      stopPropagation(e);
      onClear();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;
      handleKeyboardActivation(e, () => {
        stopPropagation(e);
        onClear();
      });
    };

    return (
      <ClearButtonContainer position={position} className={className}>
        <ClearButtonElement
          ref={ref}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-disabled={disabled}
          {...(testId && { 'data-testid': testId })}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          ✕
        </ClearButtonElement>
      </ClearButtonContainer>
    );
  },
);

ClearButton.displayName = 'ClearButton';

export default ClearButton;
